import type { FinsterQueryDataSource } from "./api/finster/constants";

/**
 * Resolved research scope for one query run or composer session.
 *
 * User files and ordinary Finster data sources are independent selections.
 * Both hosts (Excel and PowerPoint) share these helpers so the composer and
 * submit-time snapshots preserve the same additive scope.
 */
export interface ResearchSelection {
  dataSources: FinsterQueryDataSource[];
  userFileIds: string[];
}

/** Normalizes file ids to a de-duplicated, trimmed string list. */
export function normalizeSelectedUserFileIds(fileIds: readonly string[]): string[] {
  return Array.from(
    new Set(
      fileIds
        .filter((fileId): fileId is string => typeof fileId === "string")
        .map((fileId) => fileId.trim())
        .filter(Boolean),
    ),
  );
}

/**
 * Reconciles persisted or in-flight data-source picks with what the
 * current session is allowed to use. Unknown sources are dropped; an
 * empty selection stays empty rather than falling back to "all available".
 */
export function resolveSessionDataSources(
  availableDataSources: readonly FinsterQueryDataSource[],
  selectedDataSources?: readonly FinsterQueryDataSource[],
): FinsterQueryDataSource[] {
  const availableSourceSet = new Set(availableDataSources);
  const candidateSources = selectedDataSources ?? availableDataSources;
  return Array.from(
    new Set(
      candidateSources.filter((source) => availableSourceSet.has(source)),
    ),
  );
}

/**
 * Applies a Your Files selection without changing ordinary data sources.
 */
export function selectUserFiles(
  currentDataSources: readonly FinsterQueryDataSource[],
  fileIds: readonly string[],
): ResearchSelection {
  const userFileIds = normalizeSelectedUserFileIds(fileIds);
  return {
    dataSources: [...currentDataSources],
    userFileIds,
  };
}

/**
 * Applies a data-source selection without changing user file ids.
 */
export function selectDataSources(
  currentUserFileIds: readonly string[],
  dataSources: readonly FinsterQueryDataSource[],
): ResearchSelection {
  return {
    dataSources: [...dataSources],
    userFileIds: normalizeSelectedUserFileIds(currentUserFileIds),
  };
}

interface RunResearchSelectionInput {
  dataSources: readonly FinsterQueryDataSource[];
  userFileIds?: readonly string[];
}

/**
 * Snapshots the already-resolved session research scope for one query run.
 */
export function snapshotRunResearchSelection({
  dataSources,
  userFileIds: selectedUserFileIds,
}: RunResearchSelectionInput): ResearchSelection {
  const userFileIds = normalizeSelectedUserFileIds(
    selectedUserFileIds ?? [],
  );
  return {
    dataSources: [...dataSources],
    userFileIds,
  };
}

/** Adds the Your Files wire marker without changing composer selection state. */
export function appendUserFilesDataSource(
  dataSources: readonly string[],
  hasUserFiles: boolean,
): string[] {
  const normalizedDataSources = Array.from(
    new Set(dataSources.map((source) => source.trim()).filter(Boolean)),
  );
  if (hasUserFiles && !normalizedDataSources.includes("USER_FILES")) {
    normalizedDataSources.push("USER_FILES");
  }
  return normalizedDataSources;
}

/** Human-readable scope label when a run is limited to uploaded files. */
export function getUserFilesResearchScopeText(userFileCount: number): string {
  return `Your Uploaded Files (${userFileCount} selected)`;
}
